import AIChatWidget from '@/components/aichat/AiChatWidget';
import SdgIconStrip from '@/components/landing/sdg-icon-strip';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { Calendar, ChevronLeft, ChevronRight, ExternalLink, Share2, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

type ResponsiveImage = {
    src: string;
    srcSet?: string | null;
    sizes?: string | null;
};

type NewsImage = {
    image: string;
    url: string;
    responsive?: ResponsiveImage | null;
};

type SdgTag = {
    sdg_number: number;
    short_title: string;
    slug: string;
    image?: string | null;
};

type NewsItem = {
    id: number;
    title: string;
    category: string;
    description: string;
    image_url: string;
    images: NewsImage[];
    link: string;
    external_link?: string | null;
    created_at?: string | null;
    sdgs?: SdgTag[];
};

type Props = {
    news: NewsItem;
    relatedNews: NewsItem[];
    previousNews?: NewsItem | null;
    nextNews?: NewsItem | null;
};

function formatNewsDate(date?: string | null): string | null {
    if (!date) {
        return null;
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
        return date;
    }

    return parsedDate.toLocaleDateString('en-PH', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

function imageAttributes(image: NewsImage) {
    return {
        src: image.responsive?.src ?? image.url,
        srcSet: image.responsive?.srcSet ?? undefined,
        sizes: image.responsive?.sizes ?? undefined,
    };
}

function truncate(text: string, limit = 110): string {
    return text.length > limit ? `${text.slice(0, limit - 3)}...` : text;
}

function descriptionParagraphs(description: string): string[] {
    return description
        .split(/\n+/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);
}

function renderLinkedText(text: string): ReactNode[] {
    return text.split(/(https?:\/\/[^\s]+)/g).map((part, index) => {
        if (!part.startsWith('http://') && !part.startsWith('https://')) {
            return part;
        }

        return (
            <a
                key={`${part}-${index}`}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-[#0A7A35] hover:text-[#08632b] hover:underline"
            >
                Open link
                <ExternalLink className="size-3.5" />
            </a>
        );
    });
}

export default function NewsShow({ news, relatedNews, previousNews, nextNews }: Props) {
    const images = useMemo<NewsImage[]>(
        () => (news.images.length > 0 ? news.images : [{ image: news.image_url, url: news.image_url }]),
        [news.image_url, news.images],
    );
    const displayedRelatedNews = useMemo(() => relatedNews.slice(0, 5), [relatedNews]);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [isSlideshowOpen, setIsSlideshowOpen] = useState(false);
    const activeImage = images[activeImageIndex] ?? images[0];
    const dateLabel = formatNewsDate(news.created_at);
    const hasMultipleImages = images.length > 1;
    const showPreviousImage = () => setActiveImageIndex((current) => (current - 1 + images.length) % images.length);
    const showNextImage = () => setActiveImageIndex((current) => (current + 1) % images.length);
    const handleShare = async () => {
        const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

        if (!currentUrl) {
            return;
        }

        try {
            if (navigator.share) {
                await navigator.share({
                    title: news.title,
                    url: currentUrl,
                });

                return;
            }

            await navigator.clipboard?.writeText(currentUrl);
        } catch {
            // Sharing can be cancelled by the visitor; no UI fallback is needed here.
        }
    };

    return (
        <LandingLayout>
            <SeoHead title={news.title} description={truncate(news.description, 155)} />

            <section className="bg-muted pb-16">
                <div className="relative min-h-[300px] overflow-hidden bg-black md:min-h-[460px]">
                    <img src={news.image_url} alt={news.title} className="absolute inset-0 h-full w-full scale-105 object-cover opacity-55" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/82 to-[#02301c]/25" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                    <div className="relative z-10 mx-auto flex min-h-[300px] max-w-7xl flex-col justify-center px-4 py-14 text-white md:min-h-[460px] md:py-18">
                        <div className="mb-5 flex flex-wrap items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span>News</span>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">{news.category}</span>
                        </div>

                        <h1 className="max-w-6xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-4xl lg:text-5xl">
                            {news.title}
                        </h1>

                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                onClick={handleShare}
                                className="inline-flex h-9 items-center justify-center rounded-full border border-white/25 bg-white/90 px-4 text-sm font-semibold text-[#102015] shadow-sm transition hover:-translate-y-0.5 hover:bg-white hover:text-[#00963f] hover:shadow-md"
                                aria-label={`Share ${news.title}`}
                            >
                                <span>Share</span>
                                <Share2 className="ml-2 size-4" aria-hidden="true" />
                            </button>

                            {dateLabel && (
                                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white/90 backdrop-blur-sm">
                                    <Calendar className="size-4" />
                                    {dateLabel}
                                </span>
                            )}

                            <SdgIconStrip sdgs={news.sdgs} className="flex flex-wrap gap-2" iconClassName="h-8 w-8 rounded-md" />
                        </div>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
                        <article className="min-w-0">
                            <div className="group bg-card border-border relative overflow-hidden rounded-2xl border shadow-sm">
                                <button type="button" onClick={() => setIsSlideshowOpen(true)} className="block w-full" aria-label="Open news image slideshow">
                                    <img
                                        {...imageAttributes(activeImage)}
                                        alt={news.title}
                                        className="aspect-[16/10] w-full object-cover"
                                        width={960}
                                        height={600}
                                    />
                                </button>

                                {hasMultipleImages && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={showPreviousImage}
                                            className="absolute top-1/2 left-4 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white opacity-90 transition hover:bg-black/75"
                                            aria-label="Previous image"
                                        >
                                            <ChevronLeft className="size-5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={showNextImage}
                                            className="absolute top-1/2 right-4 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white opacity-90 transition hover:bg-black/75"
                                            aria-label="Next image"
                                        >
                                            <ChevronRight className="size-5" />
                                        </button>
                                        <div className="absolute right-4 bottom-4 rounded-full bg-black/60 px-3 py-1 text-xs font-bold text-white">
                                            {activeImageIndex + 1} / {images.length}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="bg-card border-border mt-8 rounded-2xl border p-5 shadow-sm md:p-7">
                                <div className="text-foreground max-w-3xl space-y-4 text-sm leading-7 md:text-base md:leading-8 [overflow-wrap:anywhere]">
                                    {descriptionParagraphs(news.description).map((paragraph) => (
                                        <p key={paragraph} className="whitespace-pre-wrap">
                                            {renderLinkedText(paragraph)}
                                        </p>
                                    ))}
                                </div>
                            </div>

                            {(previousNews || nextNews) && (
                                <nav
                                    className="border-border mt-8 flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between"
                                    aria-label="News post navigation"
                                >
                                    {previousNews ? (
                                        <Link
                                            href={previousNews.link}
                                            className="inline-flex items-center gap-2 text-sm font-bold tracking-wider text-[#0A7A35] uppercase transition-colors hover:text-[#08632b]"
                                        >
                                            <ChevronLeft className="size-4 shrink-0" />
                                            Previous Post
                                        </Link>
                                    ) : null}

                                    {nextNews ? (
                                        <Link
                                            href={nextNews.link}
                                            className="inline-flex items-center gap-2 text-sm font-bold tracking-wider text-[#0A7A35] uppercase transition-colors hover:text-[#08632b] sm:ml-auto"
                                        >
                                            Next Post
                                            <ChevronRight className="size-4 shrink-0" />
                                        </Link>
                                    ) : null}
                                </nav>
                            )}
                        </article>

                        <aside>
                            <div className="bg-card border-border rounded-2xl border p-5 shadow-sm">
                                <div className="mb-4 inline-flex rounded-full bg-[#0A7A35]/10 px-3 py-1 text-xs font-bold tracking-wider text-[#0A7A35] uppercase">
                                    Related News
                                </div>
                                <h2 className="text-foreground text-xl font-bold">Related News</h2>

                                <div className="mt-5 space-y-4">
                                    {displayedRelatedNews.length > 0 ? (
                                        displayedRelatedNews.map((item) => (
                                            <Link key={item.id} href={item.link} className="group grid grid-cols-[96px_minmax(0,1fr)] gap-3">
                                                <img src={item.image_url} alt="" className="h-20 w-24 rounded-md object-cover" loading="lazy" />
                                                <span className="min-w-0">
                                                    <span className="text-muted-foreground text-xs font-semibold uppercase">{item.category}</span>
                                                    <span className="text-foreground mt-1 line-clamp-2 text-sm font-bold group-hover:text-[#0A7A35]">
                                                        {item.title}
                                                    </span>
                                                    <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0A7A35]">
                                                        View
                                                        <ChevronRight className="size-3.5" />
                                                    </span>
                                                </span>
                                            </Link>
                                        ))
                                    ) : (
                                        <p className="text-muted-foreground text-sm">More updates will appear here soon.</p>
                                    )}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            {isSlideshowOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/92 p-4">
                    <button
                        type="button"
                        onClick={() => setIsSlideshowOpen(false)}
                        className="absolute top-4 right-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                        aria-label="Close slideshow"
                    >
                        <X className="size-5" />
                    </button>

                    {hasMultipleImages && (
                        <button
                            type="button"
                            onClick={showPreviousImage}
                            className="absolute top-1/2 left-4 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                            aria-label="Previous image"
                        >
                            <ChevronLeft className="size-6" />
                        </button>
                    )}

                    <img src={activeImage.url} alt={news.title} className="max-h-[82vh] max-w-[92vw] rounded-lg object-contain shadow-2xl" />

                    {hasMultipleImages && (
                        <button
                            type="button"
                            onClick={showNextImage}
                            className="absolute top-1/2 right-4 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                            aria-label="Next image"
                        >
                            <ChevronRight className="size-6" />
                        </button>
                    )}

                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-2 text-sm font-bold text-white">
                        {activeImageIndex + 1} / {images.length}
                    </div>
                </div>
            )}

            <AIChatWidget />
        </LandingLayout>
    );
}
